import { Fragment, ReactNode, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useLocation } from 'react-router-dom';

import { CountBadge } from 'components/CountBadge';
import { useShowSavedForLater } from 'components/create-dialog/CreateDialogContext';
import { ClockIcon } from 'components/icons';
import { Tooltip } from 'components/ui/tooltip';
import useAuth from 'hooks/useAuth';
import { matchesNavPathname } from 'layout/MainLayout/Drawer/DrawerContent/Navigation/navItemMatch';
import { useMyWorkCounts, useToggleInbox } from 'layout/NewDesignLayout/Inbox';
import getMenuItems from 'menu-items';
import { useGetDraftsQuery } from 'store/api/drafts';
import { FeatureFlag } from 'types/features';
import { useIsFeatureEnabled } from 'utils/useFeatureFlags';
import { isMenuItemVisible, isSectionVisible, type MenuAccess } from 'menu-items/menuVisibility';
import type { NavItemType } from 'types/menu';
import { cn } from 'utils/cn';
import { t } from 'utils/getTranslations';
import { FOCUS_RING, FOCUS_RING_INSET } from '../../focusRing';
import MenuLink from './MenuLink';
import NavSection from './NavSection';

const isSection = (entry: NavItemType) => entry.type === 'collapse';

const isEntryVisible = (entry: NavItemType, user: MenuAccess) =>
    isSection(entry) ? isSectionVisible(entry, user) : isMenuItemVisible(entry, user);

/** The entries the guided tour points at, by id. */
const TOUR_TARGETS: Record<string, string> = { inbox: 'sidebar-inbox', admin: 'sidebar-admin' };

/** What each My work badge counts, by entry id: read after the number, which alone says nothing. */
const COUNT_NOUNS: Record<string, string> = { inbox: 'menu.count-unread', tasks: 'menu.count-open' };

/**
 * My work's last row, while the user sees drafts and the flag is on: it counts them and opens their list. Not a menu
 * entry: it goes nowhere, and no permission hides it.
 */
const SAVED_FOR_LATER: NavItemType = {
    id: 'saved-for-later',
    type: 'item',
    title: <FormattedMessage id="create-dialog.saved.title" />,
    icon: ClockIcon,
};

/** A record's tab (`/matter/12/general`) belongs to the record's list, so only the path up to its id is matched. */
const recordPath = (pathname: string) => pathname.replace(/(\/\d+)\/.*$/, '$1');

/**
 * The first visible link, in menu order, that owns the pathname, and the section it sits in. A path no
 * link owns belongs to its parent: a list's tab (`/invoices/main`) or form (`/delegations/new`) lights the list.
 */
function findActive(groups: NavItemType[], pathname: string, user: MenuAccess) {
    const entries = groups.flatMap((group) => group.children ?? []);
    for (let path = recordPath(pathname); path; path = path.slice(0, path.lastIndexOf('/'))) {
        for (const entry of entries) {
            const link = (isSection(entry) ? (entry.children ?? []) : [entry]).find(
                (item) => isMenuItemVisible(item, user) && matchesNavPathname(path, item.url, item.altActiveUrls)
            );
            if (link) {
                return { linkId: link.id, sectionId: isSection(entry) ? entry.id : undefined };
            }
        }
    }
    return { linkId: undefined, sectionId: undefined };
}

type LinkProps = {
    item: NavItemType;
    collapsed: boolean;
    active: boolean;
    /** Drawn as a badge when above zero, as a dot on the rail. */
    count?: number;
    /** What `count` counts, for assistive tech: it follows the number in the entry's name. */
    countNoun?: string;
    /** Makes the entry a button doing this instead of a link. */
    onActivate?: () => void;
};

/**
 * An entry straight under a group: its icon, label and count, or on the rail the icon with a dot for the
 * count and the label as a tooltip.
 */
function NavLink({ item, collapsed, active, count, countNoun, onActivate }: LinkProps) {
    const Icon = item.icon;
    const className = cn(
        // It covers the 1px to the next entry, so the hover and a click there do not drop between them.
        'relative flex items-center rounded-md after:absolute after:inset-x-0 after:top-full after:h-px',
        collapsed
            ? ['size-9 justify-center pointer-coarse:size-11', FOCUS_RING]
            : ['h-8 w-full gap-2.5 px-2.5 pointer-coarse:h-11', FOCUS_RING_INSET],
        active
            ? 'bg-sidebar-accent text-sidebar-accent-foreground font-semibold [&_svg[stroke]]:stroke-2'
            : 'hover:bg-sidebar-hover'
    );
    const content = (
        <>
            {Icon && <Icon className="size-[17px]" />}
            <span className={collapsed ? 'sr-only' : 'sidebar-fade min-w-0 flex-1 truncate text-start'}>
                {item.title}
            </span>
            {count && collapsed ? (
                <span className="bg-secondary ring-sidebar absolute end-1.5 top-1.5 size-2 rounded-full ring-2">
                    <span className="sr-only">
                        {count}
                        {countNoun ? <span className="sr-only"> {countNoun}</span> : null}
                    </span>
                </span>
            ) : count ? (
                <CountBadge count={count} noun={countNoun} className="sidebar-fade" />
            ) : null}
        </>
    );
    const row = onActivate ? (
        <button
            type="button"
            onClick={onActivate}
            className={cn(
                'cursor-pointer transition-[scale] duration-(--press-dur) ease-out motion-safe:not-aria-[haspopup]:will-change-transform motion-safe:active:not-aria-[haspopup]:scale-(--press-scale)',
                className
            )}
        >
            {content}
        </button>
    ) : (
        <MenuLink item={item} aria-current={active ? 'page' : undefined} className={className}>
            {content}
        </MenuLink>
    );
    return collapsed ? (
        <Tooltip content={item.title} side="right">
            {row}
        </Tooltip>
    ) : (
        row
    );
}

type Props = {
    /** On the rail: icons alone, each section's links in a popover. */
    collapsed: boolean;
    /** Drawn between My work and the Firm sections. */
    pinned?: ReactNode;
};

/**
 * The new design's menu: My work, then the Firm sections, with the active section expanded, then Admin. A group
 * none of whose entries the user can see is left out, rule and gap included. My work ends with Saved for later
 * while the user sees drafts.
 */
function Navigation({ collapsed, pinned }: Props) {
    const { user } = useAuth();
    const { pathname } = useLocation();
    const allGroups = useMemo(() => getMenuItems('NEW').items, []);
    const groups = allGroups.filter((group) => group.children?.some((entry) => isEntryVisible(entry, user)));
    const active = findActive(groups, pathname, user);
    const counts = useMyWorkCounts();
    const toggleInbox = useToggleInbox();
    const myWorkCounts: Record<string, number | undefined> = { inbox: counts.inbox, tasks: counts.tasks };
    const showSaved = useShowSavedForLater();
    const isDraftsEnabled = useIsFeatureEnabled(FeatureFlag.SAVE_AS_DRAFT);
    const { data: saved = [] } = useGetDraftsQuery(undefined, { skip: !isDraftsEnabled || !showSaved });

    // Entering another section's page expands it and collapses the rest; a page outside every section
    // leaves them as they are. In between, the section headers are the user's.
    const [expanded, setExpanded] = useState<ReadonlySet<string>>(
        () => new Set(active.sectionId ? [active.sectionId] : [])
    );
    const [prevSectionId, setPrevSectionId] = useState(active.sectionId);
    if (active.sectionId !== prevSectionId) {
        setPrevSectionId(active.sectionId);
        if (active.sectionId) {
            setExpanded(new Set([active.sectionId]));
        }
    }

    const setSectionExpanded = (id: string, open: boolean) =>
        setExpanded((previous) => {
            const next = new Set(previous);
            if (open) next.add(id);
            else next.delete(id);
            return next;
        });

    return (
        <div className={cn('flex flex-col', collapsed ? 'items-center' : 'gap-3.5 px-3 pb-7')}>
            {groups.map((group) => {
                const headingId = `menu-${group.id}-heading`;
                return (
                    <Fragment key={group.id}>
                        <div
                            data-tour={group.id === 'firm' ? 'sidebar-sections' : undefined}
                            className={cn('flex flex-col', collapsed && 'items-center')}
                        >
                            {collapsed && <span aria-hidden className="bg-sidebar-rule my-1.5 h-px w-6" />}
                            {group.title ? (
                                <div
                                    id={headingId}
                                    className={
                                        collapsed
                                            ? 'sr-only'
                                            : 'sidebar-fade text-label text-sidebar-muted px-2.5 pb-1 uppercase'
                                    }
                                >
                                    {group.title}
                                </div>
                            ) : null}
                            <ul aria-labelledby={group.title ? headingId : undefined} className="flex flex-col gap-px">
                                {group.children
                                    ?.filter((entry) => isEntryVisible(entry, user))
                                    .map((entry) =>
                                        isSection(entry) ? (
                                            <NavSection
                                                key={entry.id}
                                                section={entry}
                                                collapsed={collapsed}
                                                expanded={expanded.has(entry.id!)}
                                                onExpandedChange={(open) => setSectionExpanded(entry.id!, open)}
                                                activeLinkId={active.linkId}
                                            />
                                        ) : (
                                            <li key={entry.id} data-tour={TOUR_TARGETS[entry.id!]}>
                                                <NavLink
                                                    item={entry}
                                                    collapsed={collapsed}
                                                    active={entry.id === active.linkId}
                                                    count={myWorkCounts[entry.id!]}
                                                    countNoun={COUNT_NOUNS[entry.id!] && t(COUNT_NOUNS[entry.id!])}
                                                    onActivate={entry.id === 'inbox' ? toggleInbox : undefined}
                                                />
                                            </li>
                                        )
                                    )}
                                {group.id === 'my-work' && showSaved && saved.length > 0 ? (
                                    <li>
                                        <NavLink
                                            item={SAVED_FOR_LATER}
                                            collapsed={collapsed}
                                            active={false}
                                            count={saved.length}
                                            countNoun={t('menu.count-items', { count: String(saved.length) })}
                                            onActivate={showSaved}
                                        />
                                    </li>
                                ) : null}
                            </ul>
                        </div>
                        {group.id === 'my-work' ? pinned : null}
                    </Fragment>
                );
            })}
        </div>
    );
}

export default Navigation;
